import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getClientDetails, updateClientDetails, getFaxTemplates, getSensitiveFields } from "./ClientView.actions";
import { CLIENT_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/ClientViewConstants";
import ConfirmationModal from "components/ConfirmationModal";
import Spinner from "components/Spinner";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import './ClientView.css';
import { useConfigForm } from "../configTool/useConfigForm";
import { validateClientEditForm, normalizePhoneDigits } from "../configTool/configFormValidators";
import {
  ConfigReadOnlyBanner,
  SensitiveFieldsConfirmationBody,
} from "../configTool/ConfigFormComponents";
import { ClientFormSections } from "../configTool/ClientFormSections";
import ConfigStickyActionBar from "../configTool/ConfigStickyActionBar";
import ConfigBreadcrumb from "../configTool/ConfigBreadcrumb";
import { useUnsavedChangesWarning } from "../configTool/useUnsavedChangesWarning";

const ClientView = () => {
  const { clientInfoUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    details: clientDetails,
    loadingDetails,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.clientView
  );

  const client =
    clientDetails &&
    String(clientDetails.clientInfoUuid || clientDetails.id) === String(clientInfoUuid)
      ? clientDetails
      : null;

  const {
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changedSensitiveFields, setChangedSensitiveFields] = useState([]);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  const initialFormValues = useMemo(() => {
    if (!client) return null;
    return {
      ...client,
      supportNumber: normalizePhoneDigits(client.supportNumber),
      faxNumberInbound: normalizePhoneDigits(client.faxNumberInbound),
      prescriberCallActivitiesEnabled: client.prescriberCallActivitiesEnabled === true,
      pharmacyCallActivitiesEnabled: client.pharmacyCallActivitiesEnabled === true,
      useNvpSourcePricing: client.useNvpSourcePricing === true,
      excludeStarSwitches: client.excludeStarSwitches === true,
      excludeStarDeprescriptions: client.excludeStarDeprescriptions === true,
      polypharmacyEnabled: client.polypharmacyEnabled === true,
      deprescriptionConfirmationEnabled: client.deprescriptionConfirmationEnabled === true,
      cobEnabled: client.cobEnabled === true,
      isRxAdvocateEnabled: client.isRxAdvocateEnabled === true,
      language: client.language || '',
      populationType: client.populationType || '',
      faxTemplate: client.faxTemplate || '',
    };
  }, [client]);

  const {
    formState,
    originalFormState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    handleBlur,
    shouldShowError,
    runValidation,
  } = useConfigForm({
    initialValues: initialFormValues,
    validate: validateClientEditForm,
    entityKey: clientInfoUuid,
  });

  const { confirmDiscard, allowNextNavigation } = useUnsavedChangesWarning(canManage && isDirty);

  useEffect(() => {
    dispatch(getClientDetails(clientInfoUuid));
    dispatch(getFaxTemplates());
    dispatch(getSensitiveFields(CLIENT_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, clientInfoUuid]);

  const leaveClientPage = () => {
    allowNextNavigation();
    const customerUuid = client?.customerInfoUuid || location.state?.customerInfoUuid;
    if (customerUuid) {
      navigate(`/admin/customer/${customerUuid}`);
      return;
    }
    navigate(-1);
  };

  const handleModalConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const proceedWithSave = () => {
    dispatch(updateClientDetails(clientInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CLIENT_DETAILS_SUCCESS") {
          leaveClientPage();
        }
      }
    );
  };

  const handleCancel = () => {
    if (!confirmDiscard()) return;
    leaveClientPage();
  };

  const handleSave = () => {
    if (!runValidation()) {
      return;
    }

    const changedFields = sensitiveFields.filter(
      (field) => originalFormState && originalFormState[field] !== formState[field]
    );
    if (changedFields.length > 0) {
      setChangedSensitiveFields(changedFields);
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  if (loadingDetails || loadingFaxTemplates || !client || !formState) {
    return <Spinner />;
  }

  const customerInfoUuid =
    client.customerInfoUuid || location.state?.customerInfoUuid;
  const customerName = client.customerName || location.state?.customerName;

  return (
    <Container fluid className="client-view-page">
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleModalConfirmSave}
        title="Confirm Save"
        body={<SensitiveFieldsConfirmationBody changedFields={changedSensitiveFields} />}
      />

      <ConfigBreadcrumb
        items={[
          { label: "Customers", to: "/admin/customers" },
          ...(customerName
            ? [{
                label: customerName,
                to: customerInfoUuid ? `/admin/customer/${customerInfoUuid}` : undefined,
              }]
            : []),
          { label: client.clientName },
        ]}
      />

      {canManage && (
        <ConfigStickyActionBar
          title={client.clientName}
          subtitle="Client configuration"
          onCancel={handleCancel}
          onSave={handleSave}
          isDirty={isDirty}
          isSaving={updating}
        />
      )}

      {!canManage && (
        <Row className="mb-3">
          <Col md={12}>
            <h5 className="mb-0">{client.clientName}</h5>
          </Col>
        </Row>
      )}

      {isReadOnly && (
        <Row>
          <Col md={12}>
            <ConfigReadOnlyBanner />
          </Col>
        </Row>
      )}

      <Row className="mt-2">
        <Col md={12}>
          <div className="client-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}
            <Form>
              <ClientFormSections
                formState={formState}
                errors={errors}
                onInputChange={handleInputChange}
                onCheckboxChange={handleCheckboxChange}
                onBlur={handleBlur}
                shouldShowError={shouldShowError}
                faxTemplates={faxTemplates}
                loadingFaxTemplates={loadingFaxTemplates}
                isReadOnly={isReadOnly}
                sensitiveFields={sensitiveFields}
                disabledFields={{
                  clientName: true,
                  clientNameAbbreviation: true,
                  callActivities: true,
                }}
              />
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ClientView;
